import type { ZodError, ZodType, ZodTypeDef } from "zod"

import { isDefined } from "./Type"

interface NetworkInformation {
  effectiveType?: string // Connection type, e.g., '4g', '3g'
  downlink?: number // Approximate download speed in Mbps
  rtt?: number // Round-trip time in ms
}

interface NavigatorWithConnection extends Navigator {
  connection?: NetworkInformation
  mozConnection?: NetworkInformation
  webkitConnection?: NetworkInformation
}

/*
 * Thin wrapper around fetch() that ensures type safety via body validators and explicitly typed error results.
 *
 * Example:
 * request(
    "https://www.anapioficeandfire.com/api/books",
    {
      okType: array(object({ url: string() })),
      signal: controller.signal
    }
  )
 */
export async function request<BODY>(
  url: string | URL,
  options: RequestOptions<BODY>,
): Promise<BODY> {
  const {
    okType,
    signal: fetchSignal,
    headers: fetchHeaders,
    body: fetchBody,
    timeout = 15_000,
    ...fetchOptions
  } = options

  const headers = new Headers(fetchHeaders)

  if (!headers.has("Content-Type") && fetchBody)
    headers.set("Content-Type", "application/json")

  // https://developer.mozilla.org/en-US/docs/Web/API/FormData/Using_FormData_Objects#sending_files_using_a_formdata_object
  if (headers.get("Content-Type") === "multipart/form-data")
    headers.delete("Content-Type")

  const token = getBearerToken()
  if (token) headers.set("Authorization", `Bearer ${token}`)

  const body =
    headers.get("Content-Type") === "application/json"
      ? JSON.stringify(fetchBody)
      : (fetchBody as RequestInit["body"])

  let timedOut = false

  try {
    // An artificial timeout on a mutative HTTP method can be dangerous because the side effect
    // might still happen on the server but the client won't know till it's refreshed.
    const canTimeout = !options.method || options.method === "GET"

    const signal = mergeAbortSignals([fetchSignal].filter(isDefined))

    if (canTimeout) {
      setTimeout(() => {
        timedOut = true
      }, timeout)
    }

    const infoBefore = {
      online: navigator.onLine,
      visibilityState: document.visibilityState,
      connectionInfo: getConnectionInfo(),
    }
    const pathname = window.location.pathname
    const requestStart = Date.now()

    const response = await fetch(url, {
      ...fetchOptions,
      body,
      headers,
      signal,
    })

    if (timedOut) {
      const requestDuration = Date.now() - requestStart
      const googleLatency = await pingGoogleLatency()

      throw new RequestError(url, {
        type: "timeout",
        status: response.status,
        info: {
          ["x-amzn-trace-id"]: response.headers.get("x-amzn-trace-id"),
          ["x-amzn-requestid"]: response.headers.get("x-amzn-requestid"),
          infoBefore,
          infoAfter: {
            online: navigator.onLine,
            visibilityState: document.visibilityState,
            connectionInfo: getConnectionInfo(),
          },
          requestDuration: `${requestDuration} ms`,
          googlePingLatency: googleLatency ? `${googleLatency} ms` : "Failed",
          pathname,
        },
      })
    }

    const { status } = response
    const textResponse = response.clone()
    const responseBody: unknown = await response
      .json()
      .catch((_) => textResponse.text())

    if (response.ok) {
      const parsed = okType.safeParse(responseBody)

      if (!parsed.success)
        throw new RequestError(url, {
          type: "validation",
          status,
          cause: parsed.error,
        })

      return parsed.data
    } else {
      throw new RequestError(url, { type: "http", status, body: responseBody })
    }
  } catch (err) {
    // If it's our own Error, just rethrow.
    if (isRequestError(err)) throw err

    // Recover abort and timeout errors with a more specific type.
    if (isAbortError(err)) throw new RequestError(url, { type: "abort" })

    // Unexpected or impossible to catch sooner error (CORS, etc)
    throw new RequestError(url, prepareUnknownError(err))
  }
}

interface RequestOptions<BODY> extends Omit<RequestInit, "body"> {
  okType: ZodType<BODY, ZodTypeDef, unknown>
  timeout?: number
  body?: RequestInit["body"] | object
}

export class RequestError extends Error {
  constructor(
    url: string | URL,
    public detail: RequestErrorDetail,
  ) {
    super(JSON.stringify({ url, ...detail }, null, 2))
    this.name = "RequestError"
  }
}

function prepareUnknownError(error: unknown): RequestErrorDetail {
  return {
    type: "unknown",
    cause: errorToPlainObject(error),
    info: {
      pathname: window.location.pathname,
      connectionInfo: getConnectionInfo(),
      visibilityState: document.visibilityState,
      online: navigator.onLine,
    },
  }
}

// Error instances don't have a toJSON method and none of their properties
// are enumerable so nothing is serialized with JSON.stringify.
// This fixes it.
function errorToPlainObject(error: unknown) {
  if (error instanceof Error)
    return {
      name: error.name,
      message: error.message,
      stack: error.stack,
    }

  return error
}

export type RequestErrorDetail =
  | { type: "http"; status: number; body: unknown }
  | { type: "abort" }
  | { type: "timeout"; status: number; info: unknown }
  | {
      type: "unknown"
      cause: unknown
      info: unknown
    }
  | { type: "validation"; status: number; cause: ZodError<unknown> }

export function isRequestError(param: unknown): param is RequestError {
  return param instanceof RequestError
}

export function isAuthError(param: unknown) {
  if (!isRequestError(param)) return false

  if (param.detail.type !== "http") return false

  // Some of our services return 403 when they should really return 401 :(
  // For instance, user-service.prismic.io/profile returns 403 when the session is expired
  // (User is not authorized to access this resource with an explicit deny)
  return param.detail.status === 401 || param.detail.status === 403
}

function mergeAbortSignals(signals: AbortSignal[]) {
  const controller = new AbortController()

  function onAbort() {
    controller.abort()
    for (const signal of signals) {
      signal.removeEventListener("abort", onAbort)
    }
  }

  for (const signal of signals) {
    if (signal.aborted) {
      onAbort()
      break
    }
    signal.addEventListener("abort", onAbort)
  }

  return controller.signal
}

function isAbortError(error: unknown): error is DOMException {
  return error instanceof DOMException && error.name === "AbortError"
}

let getBearerToken: () => string = () => ""

// Externally register a way to provide a Bearer token, so this module remains decoupled from App concerns.
// Also this function could be called conditionally.
export function registerBearerTokenGetter(fn: () => string) {
  getBearerToken = fn
}

// Makes sure that in dev environments request credentials (cookies) aren't required (we use a header instead)
// Sending the credentials even though we use the Authorization header would lead to a CORS error.
// "include" ensures that credentials are always sent, whether the API is on the same exact origin (sub domains don't count) or not.
// This function is only needed when dealing with APIs hosted on a different origin than Page Builder, since the default of "same-origin" works well otherwise.
export function getRequestCredentials(
  authStrategy: "cookie" | "token",
): RequestInit["credentials"] {
  return authStrategy === "token" ? "omit" : "include"
}

function getConnectionInfo():
  | Record<keyof NetworkInformation, string>
  | undefined {
  const navigatorWithConnection = navigator as NavigatorWithConnection

  if ("connection" in navigatorWithConnection) {
    const connection =
      navigatorWithConnection.connection ||
      navigatorWithConnection.mozConnection ||
      navigatorWithConnection.webkitConnection

    if (connection) {
      return {
        effectiveType: connection.effectiveType || "unknown",
        downlink: `${connection.downlink ?? "unknown"} Mbps`,
        rtt: `${connection.rtt ?? "unknown"} ms`,
      }
    }
  }
  return undefined
}

// NOTE: This is something experimental to understand latency.
async function pingGoogleLatency() {
  try {
    const start = Date.now()
    // Make a lightweight request to Google's servers
    await fetch("https://www.google.com", {
      method: "HEAD",
      mode: "no-cors",
      cache: "no-store",
    })
    const duration = Date.now() - start
    return duration
  } catch (error) {
    // Failed to ping google, network might be down.
    return null
  }
}
